<template>
  <div>
    <!-- Measures table -->
    <div class="card pagination-margin"
         qid="measure-section"
         id="update_measures"
    >
      <div class="card-body">
        <div class="row">
          <div class="col-sm-6">
            <div class="float-left">
              <h5 class="mb-2" qid="measure-table-title" v-if="!showOnTask">
                {{ $t('systems.tab.controls') }}
              </h5>

              <span v-if="!showOnTask">
                <span class="cursor-pointer task-label-open mr-2"
                      v-if="getTask('update_measures')"
                >
                  <UpdateSingleTaskModal
                      :task="getTask('update_measures')"
                      @reloadContent="reloadContent"
                      :editIcon="true"
                  ></UpdateSingleTaskModal>
                </span>
                <span v-if="getGuide('update_measures')">
                  <GuideButton :guide="getGuide('update_measures')"></GuideButton>
                </span>
              </span>

            </div>
            <div class="validation-info" qid="view-ac-validation" v-if="!showOnTask">
              <span v-if="validation && validation.validation === 1" qid="access-control-validation-approved">
                <font-awesome-icon icon="check-circle" class="icon-success"/>
                {{ $t('system.approved') }}
              </span>
              <span v-if="validation && validation.validation === 0" qid="access-control-validation-rejected">
                <font-awesome-icon icon="times-circle" class="icon-danger"/>
                {{ $t('system.rejected') }}
              </span>
              <span v-if="validation" class="text-muted font-italic">
                {{ $t('system.by') }}
                <span v-if="validation.created_by" qid="view-ac-validation-by">
                  {{ validation.created_by.name }}
                </span>
              </span>
              <span v-if="validation" class="text-muted font-italic">
                {{ $t('system.on') }}
                <span v-if="validation.created_at" qid="view-ac-validation-date">
                  {{ validation.created_at | formatDate }}
                </span>
              </span>
            </div>

          </div>
          <div class="col-sm-6 text-right">
            <span class="btn btn-success cursor-pointer float-right"
                  @click="showCreateMeasureModal()"
                  qid="retention-rule-create-button"
                  v-if="isGranted([permissions.SYSTEM_MEASURE_CREATE, permissions.SYSTEM_MEASURE_CREATE_OWN], system) && (!getClient.parent || (getClient.parent && system.partition))"
            >
              <font-awesome-icon icon="plus"/> {{ $t('activities.measures.add_new') }}
            </span>
            <MeasureCreateForm @reloadContent="updateMeasures" v-if="isGranted(permissions.MAINTENANCE_VIEW) && checkIfAdmin()"></MeasureCreateForm>
            <ValidationForm
                :object="system"
                type="measure"
                class="float-right"
                scope="validate_measures"
                @validationModalClose="handleValidationModalClose"
                v-if="getClient.gdprAdvancedModule && !showOnTask && isGranted([permissions.SYSTEM_MEASURE_VALIDATE, permissions.SYSTEM_MEASURE_VALIDATE_OWN], system)"
            ></ValidationForm>
          </div>
        </div>

        <table class="table table-responsive-md table-list-secondary">
          <thead>
          <tr>
            <th scope="col"
                class="cursor-pointer"
                @click="selectSort('name')"
                qid="sort-s-name"
            >
              {{ $t('activities.measures.table.name') }}
              <span class="ml-2">
                <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'name'"/>
                <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'name'"/>
                <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'name'"/>
              </span>
            </th>
            <th scope="col">
              {{ $t('activities.measures.table.category') }}
            </th>
            <th scope="col">
              {{ $t('activities.measures.table.value') }}
            </th>
            <th scope="col"
                class="cursor-pointer"
                @click="selectSort('updated_at')"
                qid="sort-s-name"
            >
              {{ $t('system.last_updated') }}
              <span class="ml-2">
                <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'updated_at'"/>
                <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'updated_at'"/>
                <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'updated_at'"/>
              </span>
            </th>
            <th width="180"></th>
          </tr>
          </thead>
          <tbody>
            <tr v-for="(object, index) in measureTableItems"
                :id="'table-row-' + index"
                :class="{'alert-danger': object.measure.value != 'none' && !object.response}"
            >
              <b-tooltip
                  v-if="object.measure.value != 'none' && !object.response"
                  ref="tooltip"
                  :target="'table-row-' + index"
              >
                {{ $t('system.missing_value') }}
              </b-tooltip>
              <td>
                <span v-if="object.measure && object.measure.name"
                      @click="openViewMeasureModal(object)"
                      class="btn btn-link btn-sm text-left cursor-pointer"
                >
                    {{ object.measure.name }}
                </span>
              </td>
              <td>
                <span v-if="object.measure && object.measure.measure_category && object.measure.measure_category.id" class="text-normal">
                  {{ object.measure.measure_category.name }}
                </span>
              </td>
              <td>
                <span v-if="object.response" class="text-normal">
                  <span v-if="object.measure.value == 'date'">{{ object.response  | formatDate }}</span>
                  <span v-if="object.measure.value == 'number'">{{ getNumberValue(object.response) }} {{ object.measure.currency }}</span>
                  <span v-if="object.measure.value == 'dropdown'">{{ object.response }}</span>
                  <br style="height:0px;"/>
                  <span class="text-muted">
                    {{ $t('maintenance.measures.values.' + $options.filters.slugify(object.measure.value)) }}
                  </span>
                </span>
              </td>
              <td>
                <span class="text-normal">
                  {{ object.updated_at | formatDate }}
                </span>
              </td>
              <td class="text-right">
                <span v-if="object.measure.value != 'none' && isGranted([permissions.SYSTEM_MEASURE_EDIT, permissions.SYSTEM_MEASURE_EDIT_OWN], system) && (!getClient.parent || (getClient.parent && system.partition))"
                      class="btn btn-edit"
                      qid="edit-p-modal"
                      @click="showEditMeasureModal(object)"
                >
                  <font-awesome-icon icon="pen"/>
                </span>
                <span v-if="object.measure && object.measure.name"
                      @click="openViewMeasureModal(object)"
                      class="btn btn-view cursor-pointer"
                >
                  <font-awesome-icon icon="eye"/>
                </span>
                <span class="btn btn-delete cursor-pointer"
                      qid="delete-measure-modal"
                      @click="showMeasureConfirmDeleteModal(object)"
                      v-if="isGranted([permissions.SYSTEM_MEASURE_DELETE, permissions.SYSTEM_MEASURE_DELETE_OWN], system) && (!getClient.parent || (getClient.parent && system.partition))"
                >
                  <font-awesome-icon icon="trash-alt" />
                </span>
              </td>
            </tr>
            <tr v-if="!measureLoading && !measureTableItems.length" qid="no-results-row-rr" class="text-center">
              <td colspan="10">
                <h5 class="mt-3">
                  {{ $t('system.no_results') }}
                </h5>
              </td>
            </tr>
          </tbody>
        </table>
        <br>
        <div class="text-center mb-4" v-if="!showOnTask">
          <span class="cursor-pointer"
                @click="acValidationShow = !acValidationShow"
                qid="access-control-validation-expand"
          >
            <span class="mr-2">{{ $t('system.view_validation_log') }}</span>
            <font-awesome-icon icon="chevron-down" v-if="!acValidationShow"/>
            <font-awesome-icon icon="chevron-up" v-if="acValidationShow"/>
          </span>
        </div>
        <hr class="mb-0" v-show="acValidationShow && !showOnTask">
        <ValidationList
            :object="system"
            type="measure"
            :reloadList="updateValidationList"
            @validationListUpdated="setValidation"
            v-show="acValidationShow && !showOnTask"
        ></ValidationList>
      </div>

      <div class="card-body">
        <div class="pagination row">
          <div class="col-md-6">
            <div class="row">
              <div class="float-left">
                <span class="text-muted pagination-counter">
                  {{ $t('pagination.entries_per_page') }}:
                </span>
              </div>
              <div class="float-left">
                <multiselect
                  v-model="perPage"
                  :options="perPageOptions"
                  track-by="value"
                  label="value"
                  :select-label="$t('system.dropdown_select_label')"
                  :selected-label="$t('system.dropdown_selected_label')"
                  :deselect-label="$t('system.dropdown_deselect_label')"
                  qid="create-org-form-organization-level"
                  @select="perPageChanged"
                  :placeholder="selectedPerPage"
                  @remove="perPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <b-pagination
                class="float-right"
                v-model="currentPage"
                :total-rows="rows"
                :per-page="selectedPerPage"
                aria-controls="my-table"
                @change="pageChanged"
                limit="1"
            >
              <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
              <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
              <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
              <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                <span v-if="active">{{getPerPage}}</span>
              </span>
            </b-pagination>
          </div>
        </div>
      </div>
    </div>


    <!-- Delete measure modal template -->
    <b-modal ref="delete-measure" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-measure-title">
          {{ $t('activities.measures.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('activities.measures.delete.description') }}
        <span v-if="itemToDelete.measure">{{ itemToDelete.measure.name }}</span>
      </h5>
      <h5 class="text-center">
        {{ $t('activities.measures.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideMeasureConfirmDeleteModal"
                  qid="delete-measure-cancel-button"
        >
          {{ $t('activities.measures.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteMeasure(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-measure-submit-button"
        >
          {{ $t('activities.measures.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Add new measure -->
    <b-modal ref="add-measure" hide-footer @hide="hideCreateMeasureModal">
      <div class="d-block text-center">
        <h4 qid="add-ac-title">
          {{ $t('activities.measures.create.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-ac-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="formSubmit" qid="create-ac-form">
        <div class="form-container">
          <b-form-group>
            <label qid="create-ac-pg-label">
              {{ $t('activities.measures.create.measure_label') }}
            </label>
            <multiselect
                v-model="selectedMeasure"
                :options="measureItems"
                :placeholder="$t('activities.measures.create.measure_placeholder')"
                label="name"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                @search-change="loadMeasures"
                @select="onMeasureSelected"
                track-by="slug"
                qid="create-rr-form-rr"
            ></multiselect>
          </b-form-group>
          <div v-if="selectedMeasure && selectedMeasure.description"
               class="text-center mb-3 mt-4"
          >
            <span class="cursor-pointer">
              <span class="mr-2"
                    @click="showRRDescription = !showRRDescription"
                    qid="expand-rr-description"
              >
                {{$t('activities.measures.create.measure_description_expand')}}
              </span>
              <font-awesome-icon icon="chevron-down" v-if="!showRRDescription"/>
              <font-awesome-icon icon="chevron-up" v-if="showRRDescription"/>
            </span>
          </div>
          <div v-if="selectedMeasure && selectedMeasure.description && showRRDescription" class="mb-4 mt-2">
            <span class="text-formatted">
              {{ selectedMeasure.description }}
            </span>
          </div>
          <br>
          <div v-if="selectedMeasure && selectedMeasure.measure_category && selectedMeasure.measure_category.id">
            <label class="mt-3">
              {{ $t('activities.measures.table.category') }}
            </label>
            <br>
            <span class="mr-1">
              {{ selectedMeasure.measure_category.name }}
            </span>
          </div>
          <br>
          <b-form-group>
            <label qid="create-s-name-label" v-if="selectedMeasure && selectedMeasure.id">
              {{ $t('maintenance.measures.create.value_label') }}
              ({{ selectedMeasure.value }})
            </label>

            <datepicker
                class="date-picker"
                qid="form-due-date"
                calendar-class="left"
                v-model="selectedDate"
                :clear-button="true"
                :placeholder="$t('maintenance.measures.values.date')"
                v-if="selectedMeasure && selectedMeasure.value == 'date'"
            ></datepicker>

            <div class="row" v-if="selectedMeasure && selectedMeasure.value == 'number'">
              <div class="col-sm-10">
                <b-form-input
                  qid="business-risk-amount"
                  type="number"
                  :placeholder="$t('maintenance.measures.values.number')"
                  v-model="selectedNumber"
                ></b-form-input>
              </div>
              <div class="col-sm-2">
                <h6 class="mt-2 mb-0">
                  {{ selectedMeasure.currency }}
                </h6>
              </div>
            </div>

            <multiselect
                v-model="selectedAnswer"
                :options="allAnswers"
                :multiple="false"
                :taggable="false"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('maintenance.measures.values.dropdown')"
                v-if="selectedMeasure && selectedMeasure.value == 'dropdown'"
            ></multiselect>

          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideCreateMeasureModal"
                    qid="create-rr-cancel-button"
          >
            {{ $t('activities.measures.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="create-rr-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('activities.measures.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>


    <!-- Edit measure  -->
    <b-modal ref="edit-measure" hide-footer @hide="hideEditMeasureModal" size="xl">
      <div class="d-block text-center">
        <h4 qid="add-ac-title">
          {{ $t('activities.measures.edit.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-ac-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="formEditSubmit" qid="create-ac-form">
        <div class="row">
          <div class="col-sm-6">
            <div class="card processing_type">
              <div class="row no-gutters">
                <div class="col-sm-4 card-menu">
                  <div class="card-body text-dark">
                    <p class="mt-2">{{ $t('maintenance.measures.create.name_label') }}</p>
                    <br>
                    <p>{{ $t('maintenance.measures.create.category_label') }}</p>
                    <br>
                    <p>{{ $t('maintenance.measures.create.description_label') }}</p>
                    <br>
                  </div>
                </div>
                <div class="col-sm-8">
                  <div class="card-body">

                    <div v-if="selectedTableItem && selectedTableItem.measure">
                      <p class="mt-2 text-normal">
                        <span>
                          {{ selectedTableItem.measure.name }}
                        </span>
                      </p>
                      <br>
                      <p class="text-normal">
                        <span v-if="selectedTableItem.measure.measure_category">
                          {{ selectedTableItem.measure.measure_category.name }}
                        </span>
                      </p>
                      <br>
                      <p class="text-normal">
                        <span>
                          {{ selectedTableItem.measure.description }}
                        </span>
                      </p>
                      <br>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-sm-6">
            <div class="card processing_type">
              <div class="row no-gutters" style="min-height: 200px;" v-if="selectedTableItem && selectedTableItem.measure">
                <div class="col-sm-4 card-menu">
                  <div class="card-body text-dark">
                    <p class="mt-2">{{ $t('maintenance.measures.create.value_label') }}
                      ({{ selectedTableItem.measure.value }})</p>
                    <br>
                  </div>
                </div>
                <div class="col-sm-8">
                  <div class="card-body">

                    <div class="">
                      <b-form-group>

                        <datepicker
                            class="date-picker"
                            qid="form-due-date"
                            calendar-class="left"
                            v-model="selectedDate"
                            :clear-button="true"
                            :placeholder="$t('maintenance.measures.values.date')"
                            v-if="selectedTableItem && selectedTableItem.measure && selectedTableItem.measure.value == 'date'"
                        ></datepicker>

                        <div class="row" v-if="selectedTableItem && selectedTableItem.measure && selectedTableItem.measure.value == 'number'">
                          <div class="col-sm-9">
                            <b-form-input
                              qid="business-risk-amount"
                              type="number"
                              :placeholder="$t('maintenance.measures.values.number')"
                              v-model="selectedNumber"
                            ></b-form-input>
                          </div>
                          <div class="col-sm-3">
                            <h6 class="mt-2 mb-0">
                              {{ selectedTableItem.measure.currency }}
                            </h6>
                          </div>
                        </div>

                        <multiselect
                            v-model="selectedAnswer"
                            :options="allAnswers"
                            :multiple="false"
                            :taggable="false"
                            :select-label="$t('system.dropdown_select_label')"
                            :selected-label="$t('system.dropdown_selected_label')"
                            :deselect-label="$t('system.dropdown_deselect_label')"
                            :placeholder="$t('maintenance.measures.values.dropdown')"
                            v-if="selectedTableItem && selectedTableItem.measure && selectedTableItem.measure.value == 'dropdown'"
                        ></multiselect>

                      </b-form-group>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditMeasureModal"
                    qid="create-rr-cancel-button"
          >
            {{ $t('activities.measures.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="create-rr-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('system.save') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>


    <ContentMeasureViewModal
        :object="selectedItem"
        :showModal="viewMeasureModal"
        @closeViewModal="handleCloseViewMeasureModal"
        v-if="selectedItem"
    ></ContentMeasureViewModal>

  </div>

</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Avatar from '@/components/Avatar.vue';
  import roleCheck from "@/util/ACL/roleCheck"
  import ValidationList from '@/views/Validations/ValidationList.vue';
  import ValidationForm from '@/views/Validations/ValidationForm.vue';
  import ContentMeasureViewModal from '@/views/Measures/ContentMeasureViewModal.vue';
  import MeasureCreateForm from '@/views/Measures/MeasureCreateForm.vue'
  import Datepicker from 'vuejs-datepicker';
  import UpdateSingleTaskModal from '@/views/Tasks/UpdateSingleTaskModal.vue';
  import GuideButton from '@/components/guide/GuideButton.vue';


  export default {
    name: 'MeasureTable',
    data: function() {
      return {
        form: {
          response: ''
        },
        error: '',
        loadingItems: false,
        selectedMeasure: [],
        selectedTableItem: [],
        viewRRModal: false,
        editRRModal: false,
        selectedMeasureSort: '',
        selectedMeasureDirection: '',
        acValidationShow: false,
        measureTableItems: [],
        measureLoading: true,
        measureItems: [],
        updateValidationList: false,
        itemToDelete: {},
        itemObject: {},
        reCertificationOptions: [],
        provisioningOptions: [],
        selectedRC: [],
        selectedP: [],
        validationOptions: [],
        validation: undefined,
        disabledValidationOptions: [],
        showRRDescription: false,
        buttonDisabled: false,
        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,
        selectedSort: '',
        selectedDirection: '',
        filterOpened: false,
        keywordValue: '',
        selectedItem: {},
        viewMeasureModal: false,
        selectedAnswer: [],
        selectedDate: '',
        selectedNumber: '',
        allAnswers: [],
        myGuides: [],
        myTasks: [],
        validation: {}
      }
    },
    props: {
      object: {
        type: Object,
        required: true
      },
      tasks: {
        type: Array
      },
      showOnTask: {},
      task: {}
    },
    components: {
      Avatar,
      ValidationList,
      ValidationForm,
      ContentMeasureViewModal,
      Datepicker,
      MeasureCreateForm,
      UpdateSingleTaskModal,
      GuideButton
    },
    computed: {
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getClient() {
        return this.$store.state.client
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
      },
      getReloadTasks() {
        return this.$store.state.reloadTasks
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadMeasures();
      },
      object: function (value) {
        this.system = value
        if (value && value.slug) {
          this.loadTasks('update_measures');
          this.loadTableMeasures();
        }
      },
      getReloadContent: function () {
        if (!this.showOnTask) {
          if (this.$store.state.updatedObject && this.$store.state.updatedObject.id) {
            if (this.$store.state.updatedObject.id == this.system.id && this.$store.state.updatedObject.who_am_i == this.system.who_am_i) {
              this.system = this.$store.state.updatedObject
            }
          }
          this.reloadContent()
        }
      },
      getReloadTasks: function () {
        if (!this.showOnTask) {
          this.loadTasks('update_measures')
        }
      },
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadTableMeasures()
      }, 400),
      getNumberValue(number) {
        let transformed = ''
        if (number) {
          transformed = number.toString().replace(/(\d)(?=(\d{3})+(?:\.\d+)?$)/g, "$1.")
        }
        return transformed
      },
      checkIfAdmin() {
        if (roleCheck('Client admin') || roleCheck('Partition admin')) {
          return true
        } else {
          return false
        }
      },
      reloadContent() {
        this.loadTableMeasures();
        this.loadTasks('update_measures');
      },
      loadTasks(code) {
        let self = this;
        if (this.getClient.projectModule) {
          piincHttp.get('projects/me/tasks', { params:
            {
              per_page: 1,
              status: 'pending',
              model_type: this.system.who_am_i,
              model_id: this.system.id,
              code: code,
              client: this.getClient.slug
            }
          }).then(function(response) {
            self.myTasks = []
            if (response.data.items.length) {
              self.myTasks.push(response.data.items[0])
            }
          }, function() {});
        }
      },
      setValidation(object) {
        this.validation = object
      },
      handleValidationModalClose() {
        this.updateValidationList = true
        this.loadContent()
      },
      openViewMeasureModal(object) {
        this.selectedItem = object
        this.viewMeasureModal = true
      },
      handleCloseViewMeasureModal () {
        this.selectedItem = []
        this.viewMeasureModal = false;
      },
      onMeasureSelected(object) {
        if (object.type == 'Select') {
          this.allAnswers = JSON.parse(object.type_options)
        } else {
          this.allAnswers = ['Yes', 'No']
        }
      },
      selectSort(sort) {
        // If old and new sort are same, switch just direction
        if (sort === this.selectedSort) {
          this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
        } else {
          // If different sort, set sort and set direction to default
          this.selectedDirection = 'desc'
          this.selectedSort = sort;
        }
        this.loadTableMeasures();
      },
      getTask(code) {
        let existingTask = _.find(this.myTasks, function(object) {
          return object.code == code
        });
        return existingTask
      },
      openToolbar(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'System' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'System',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      showCreateMeasureModal() {
        this.selectedAnswer = []
        this.selectedMeasure = []
        this.selectedDate = ''
        this.selectedNumber = ''
        this.$refs['add-measure'].show()
      },
      hideCreateMeasureModal() {
        this.error = ''
        this.selectedTableItem = []
        this.loadContent()
        this.$refs['add-measure'].hide()
      },
      showEditMeasureModal(object) {
        let self = this;
        this.selectedTableItem = object

        if (object.measure.value == 'dropdown') {
          if (object.measure.type == 'Select') {
            this.allAnswers = JSON.parse(object.measure.type_options)
          } else {
            this.allAnswers = ['Yes', 'No']
          }

          self.selectedAnswer = []
          if (self.selectedTableItem.response) {
            self.selectedAnswer = _.find(self.allAnswers, function(item) {
              if(item === self.selectedTableItem.response){
                return self.selectedTableItem.response;
              }
            });
          }
        }else if (object.measure.value == 'date') {
          self.selectedDate = self.selectedTableItem.response;
        }else if (object.measure.value == 'number') {
          self.selectedNumber = self.selectedTableItem.response;
        }


        this.$refs['edit-measure'].show()
      },
      hideEditMeasureModal() {
        this.error = ''
        this.selectedMeasure = []
        this.selectedAnswer = []
        this.selectedDate = ''
        this.selectedNumber = ''
        this.loadContent()
        this.$refs['edit-measure'].hide()
      },
      showMeasureConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-measure'].show()
      },
      hideMeasureConfirmDeleteModal() {
        this.itemToDelete = {}
        this.loadContent()
        this.$refs['delete-measure'].hide()
      },
      confirmDeleteMeasure(object) {
        let self = this;
        piincHttp.delete('systems/' + this.system.slug + '/measures/' + object.id).then(function() {
          self.hideMeasureConfirmDeleteModal();
          self.loadTableMeasures();
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('activities.measures.delete.success'))
        }, function() {});
      },
      formSubmit(evt) {
        this.buttonDisabled = true;
        let formData = new FormData();

        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        if (this.selectedMeasure && this.selectedMeasure.id) {
          formData.append("measure_id", this.selectedMeasure.id);
        }

        if (this.selectedAnswer && this.selectedAnswer.length > 0) {
          formData.append("response", this.selectedAnswer.toString());
        }else if (this.selectedDate) {
          formData.append("response", this.selectedDate);
        }else if (this.selectedNumber) {
          formData.append("response", this.selectedNumber);
        }

        piincHttp.post('/systems/' + this.system.slug + '/measures', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }).then(function(response) {
          self.$toastr('success', self.$t('activities.measures.create.success'))
          self.hideCreateMeasureModal();
          self.loadTableMeasures();
          self.$emit('updated');
          self.buttonDisabled = false;
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
            if (error.data.errors.measure_id) {
              self.error += ' ' + error.data.errors.measure_id[0]
            }
          }
        })
      },
      formEditSubmit(evt) {
        this.buttonDisabled = true;

        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        this.form.response = ''

        if (this.selectedAnswer && this.selectedAnswer.length > 0) {
          this.form.response = this.selectedAnswer.toString();
        }else if (this.selectedDate) {
          this.form.response = this.selectedDate;
        }else if (this.selectedNumber) {
          this.form.response = this.selectedNumber;
        }

        piincHttp.put('/systems/' + this.system.slug + '/measures/' + this.selectedTableItem.id, this.form).then(function(response) {
          self.hideEditMeasureModal();
          self.loadTableMeasures(self.currentPage);
          self.buttonDisabled = false;
          self.$toastr('success', self.$t('maintenance.measures.update.success'))
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      },
      loadContent() {
        let self = this;
        piincHttp.get('systems/' + this.system.slug, { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.system = response.data;
        }, function() {});
      },
      loadTableMeasures(page, perPage) {

        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;

        piincHttp.get('systems/' + this.system.slug + '/measures', { params:
            {
              page: pageNumber,
              per_page: perPageNumber,
              sort: this.selectedSort !== '' ? this.selectedSort : undefined,
              order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
              keyword: this.keywordValue !== '' ? this.keywordValue : undefined,
              client: this.getClient.slug
            }
        }).then(function(response) {

          self.measureLoading = false;
          self.measureTableItems = response.data.items;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function() {});
      },
      pageChanged(page) {
        // Load Third parties when page changed listener is triggered
        this.loadTableMeasures(page)
      },
      perPageChanged(perPage) {
        let self = this;

        self.selectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.selectedPerPage);
        // Load Third parties when page changed listener is triggered
        this.loadTableMeasures(1, perPage.value)
      },
      perPageRemoved() {
        let self = this;
        // Load Third parties when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadTableMeasures(1, self.selectedPerPage)

      },
      updateMeasures(object) {
        this.loadMeasures()
      },
      loadMeasures(query) {
        let self = this;
        piincHttp.get('measures', { params:
          {
            client: this.getClient.slug,
            per_page: 100,
            scope: 'system',
            keyword: query ? query : undefined
          }
        }).then(function(response) {
          self.measureItems = response.data.items
          if (self.measureItems && !self.measureItems.length && !query) {
            self.measureItems.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
    },
    created () {
      this.system = this.object
      if (this.getClient && this.getClient.slug) {
        this.loadMeasures();
      }

      if (this.system && this.system.id) {
        this.loadTasks('update_measures');
        // Table items
        this.loadTableMeasures();
        //this.validationOptions = _.cloneDeep(this.system.accessControlValidationOptions)
      }

      this.loadGuides('update_measures')
    }
  }
</script>
